import { createFileLabelFromParts } from '../../helpers/string-helper';
import { Country } from '../country';
import { EnquiryBrand } from '../enquiry-brand';
import { getUrlValueForLanguage, Language } from '../language';
import { MonthlyPayment } from '../monthly-payment';

export enum BuildType {
  DAVID_LLOYD_UK = 'david-lloyd-uk',
  DAVID_LLOYD_BELGIUM = 'david-lloyd-belgium',
  DAVID_LLOYD_GERMANY = 'david-lloyd-germany',
  DAVID_LLOYD_SPAIN = 'david-lloyd-spain',
  DAVID_LLOYD_FRANCE = 'david-lloyd-france',
  DAVID_LLOYD_IRELAND = 'david-lloyd-ireland',
  DAVID_LLOYD_ITALY = 'david-lloyd-italy',
  DAVID_LLOYD_NETHERLANDS = 'david-lloyd-netherlands',
  DAVID_LLOYD_SWITZERLAND = 'david-lloyd-switzerland',
  HARBOUR = 'harbour',
  MERIDIAN = 'meridian',
  TOGETHER_FOUNDATION = 'together-foundation',
  DAVID_LLOYD_LUXEMBOURG = 'david-lloyd-luxembourg',
}

export const DAVID_LLOYD_BUILD_TYPES = [
  BuildType.DAVID_LLOYD_UK,
  BuildType.DAVID_LLOYD_BELGIUM,
  BuildType.DAVID_LLOYD_GERMANY,
  BuildType.DAVID_LLOYD_SPAIN,
  BuildType.DAVID_LLOYD_FRANCE,
  BuildType.DAVID_LLOYD_IRELAND,
  BuildType.DAVID_LLOYD_ITALY,
  BuildType.DAVID_LLOYD_NETHERLANDS,
  BuildType.DAVID_LLOYD_SWITZERLAND,
  BuildType.DAVID_LLOYD_LUXEMBOURG,
];

const CLUB_PAGES_BUILD_WHITELIST = [
  '/clubs/landing/',
  '/clubs/home-page/',
  '/clubs/book-a-tour/',
  '/clubs/manage-your-tour/',
  '/clubs/book-a-club-welcome/',
  '/clubs/club-facilities/',
  '/clubs/enquire-now/',
  '/clubs/enquiry-form/',
  '/clubs/family-and-kids/',
  '/clubs/join-now/',
  '/clubs/membership-trial/',
  '/clubs/relax-and-socialise/',
  '/clubs/timetable/',
  '/clubs/yoga/',
  '/clubs/personal-training/',
  '/clubs/golf/',
  '/clubs/family/',
  '/clubs/live-classes/',
  '/clubs/on-demand/',
  '/clubs/club-facilities/creche/',
  '/clubs/club-facilities/exercise-classes/',
  '/clubs/club-facilities/gym/',
  '/clubs/club-facilities/kids-classes/',
  '/clubs/club-facilities/pools/',
  '/clubs/club-facilities/spa/',
  '/clubs/club-facilities/tennis/',
  '/clubs/treatments/',
  '/clubs/live-classes/',
  '/clubs/join-online/',
  '/clubs/join-online/more-details/',
  '/clubs/join-online/corporate/',
  '/clubs/join-online/TDRCapital/',
  '/clubs/join-online/friends-and-family/',
  '/clubs/join-online/young-adult/',
  '/clubs/join-online/family/',
];

const HARBOUR_CLUB_PAGES = [
  '/clubs/landing/',
  '/clubs/home-page/',
  '/clubs/book-a-tour/',
  '/clubs/manage-your-tour/',
  '/clubs/book-a-club-welcome/',
  '/clubs/club-facilities/',
  '/clubs/enquire-now/',
  '/clubs/enquiry-form/',
  '/clubs/family-and-kids/',
  '/clubs/relax-and-socialise/',
  '/clubs/timetable/',
  '/clubs/yoga/',
  '/clubs/personal-training/',
  '/clubs/golf/',
  '/clubs/family/',
  '/clubs/live-classes/',
  '/clubs/on-demand/',
  '/clubs/club-facilities/creche/',
  '/clubs/club-facilities/exercise-classes/',
  '/clubs/club-facilities/gym/',
  '/clubs/club-facilities/kids-classes/',
  '/clubs/club-facilities/pools/',
  '/clubs/club-facilities/spa/',
  '/clubs/club-facilities/tennis/',
  '/clubs/membership-trial/',
  '/clubs/join-online/',
  '/clubs/join-online/more-details/',
  '/clubs/join-online/corporate/',
  '/clubs/join-online/friends-and-family/',
  '/clubs/join-online/young-adult/',
  '/clubs/join-online/family/',
];

type LanguagesForBuild = {
  default: Language;
  secondary: Language[];
};

// When editing the languages per build type, edit the redirect on the servers template as well
export const getOrderedLanguagesForBuildType = (buildType: BuildType): LanguagesForBuild => {
  switch (buildType) {
    case BuildType.DAVID_LLOYD_UK:
    case BuildType.HARBOUR:
      return {
        default: Language.EN_GB,
        secondary: [],
      };
    case BuildType.TOGETHER_FOUNDATION:
      return {
        default: Language.EN_GB,
        secondary: [
          Language.FR_FR,
          Language.DE_DE,
          Language.CA_ES,
          Language.ES_ES,
          Language.NL_NL,
          Language.IT_IT,
        ],
      };
    case BuildType.DAVID_LLOYD_BELGIUM:
      return {
        default: Language.NL_BE,
        secondary: [Language.EN_BE, Language.FR_BE],
      };
    case BuildType.DAVID_LLOYD_FRANCE:
      return {
        default: Language.FR_FR,
        secondary: [Language.EN_FR],
      };
    case BuildType.DAVID_LLOYD_GERMANY:
      return {
        default: Language.DE_DE,
        secondary: [Language.EN_DE],
      };
    case BuildType.DAVID_LLOYD_IRELAND:
      return {
        default: Language.EN_IE,
        secondary: [],
      };
    case BuildType.DAVID_LLOYD_ITALY:
      return {
        default: Language.IT_IT,
        secondary: [Language.EN_IT],
      };
    case BuildType.DAVID_LLOYD_NETHERLANDS:
      return {
        default: Language.NL_NL,
        secondary: [Language.EN_NL],
      };
    case BuildType.DAVID_LLOYD_SPAIN:
      return {
        default: Language.ES_ES,
        secondary: [Language.CA_ES, Language.EN_ES],
      };
    case BuildType.DAVID_LLOYD_SWITZERLAND:
      return {
        default: Language.FR_CH,
        secondary: [Language.EN_CH],
      };
    case BuildType.MERIDIAN:
      return {
        default: Language.DE_DE,
        secondary: [Language.EN_DE],
      };
    case BuildType.DAVID_LLOYD_LUXEMBOURG:
      return {
        default: Language.LB_LU,
        secondary: [Language.EN_LU, Language.FR_LU, Language.DE_LU],
      };
    default:
      throw new Error(`Unexpected build type ${buildType as string} when looking up languages`);
  }
};

export const getAllLanguagesForBuildType = (buildType: BuildType): Language[] => {
  const orderedLanguages = getOrderedLanguagesForBuildType(buildType);
  return [orderedLanguages.default, ...orderedLanguages.secondary];
};

export const getClubSpecificLanguagesForBuildType = (
  languagesOverride: Language[] | undefined,
  buildType: BuildType
): Language[] => {
  return languagesOverride || getAllLanguagesForBuildType(buildType);
};

export const shouldIncludeLanguageInUrlForBuildType = (buildType: BuildType): boolean => {
  if (buildType === BuildType.MERIDIAN) {
    return true;
  }

  const languagesForBuildType = getAllLanguagesForBuildType(buildType);
  return languagesForBuildType.length !== 1;
};

export const getLanguageUrlPrefixForBuildType = (
  language: Language,
  buildType: BuildType
): string => {
  return shouldIncludeLanguageInUrlForBuildType(buildType) ? getUrlValueForLanguage(language) : '';
};

export const getPagesToBuildForBuildType = (buildType: BuildType): string[] => {
  switch (buildType) {
    case BuildType.HARBOUR:
      return HARBOUR_CLUB_PAGES;
    default:
      return CLUB_PAGES_BUILD_WHITELIST;
  }
};

type ClubDisplayValues = {
  cmsCollectionName: string;
  clubNamePrefix: string;
  displayName: string;
  copyright: string;
};

export const getClubDisplayValues = (buildType: BuildType): ClubDisplayValues => {
  switch (buildType) {
    case BuildType.DAVID_LLOYD_UK:
      return {
        cmsCollectionName: 'UK',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.HARBOUR:
      return {
        cmsCollectionName: 'Harbour',
        clubNamePrefix: 'Harbour Club',
        displayName: 'Harbour Club',
        copyright: 'Harbour Club Ltd',
      };
    case BuildType.DAVID_LLOYD_BELGIUM:
      return {
        cmsCollectionName: 'BE',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_FRANCE:
      return {
        cmsCollectionName: 'FR',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_GERMANY:
      return {
        cmsCollectionName: 'DE',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_IRELAND:
      return {
        cmsCollectionName: 'IE',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_ITALY:
      return {
        cmsCollectionName: 'IT',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_LUXEMBOURG:
      return {
        cmsCollectionName: 'LU',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_NETHERLANDS:
      return {
        cmsCollectionName: 'NL',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_SPAIN:
      return {
        cmsCollectionName: 'ES',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.DAVID_LLOYD_SWITZERLAND:
      return {
        cmsCollectionName: 'CH',
        clubNamePrefix: 'David Lloyd',
        displayName: 'David Lloyd Clubs',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.MERIDIAN:
      return {
        cmsCollectionName: 'Meridian',
        clubNamePrefix: 'David Lloyd Meridian',
        displayName: 'David Lloyd Meridian',
        copyright: 'David Lloyd Leisure Ltd',
      };
    case BuildType.TOGETHER_FOUNDATION:
      return {
        cmsCollectionName: 'Charity',
        clubNamePrefix: 'David Lloyd Together Foundation',
        displayName: 'David Lloyd Together Foundation',
        copyright: 'David Lloyd Leisure Ltd',
      };

    default:
      throw new Error(`Unexpected buildType ${buildType as string}`);
  }
};

export const shouldUseInternalClubPickerForBuildType = (buildType: BuildType) => {
  return buildType !== BuildType.DAVID_LLOYD_UK;
};

export const getEnquiryBrandForBuildType = (buildType: BuildType): EnquiryBrand => {
  switch (buildType) {
    case BuildType.DAVID_LLOYD_UK:
      return EnquiryBrand.DAVID_LLOYD_UK;
    case BuildType.DAVID_LLOYD_SPAIN:
      return EnquiryBrand.DAVID_LLOYD_SPAIN;
    case BuildType.DAVID_LLOYD_BELGIUM:
      return EnquiryBrand.DAVID_LLOYD_BELGIUM;
    case BuildType.HARBOUR:
      return EnquiryBrand.HARBOUR;
    case BuildType.DAVID_LLOYD_IRELAND:
      return EnquiryBrand.DAVID_LLOYD_IRELAND;
    case BuildType.DAVID_LLOYD_NETHERLANDS:
      return EnquiryBrand.DAVID_LLOYD_NETHERLANDS;
    case BuildType.DAVID_LLOYD_ITALY:
      return EnquiryBrand.DAVID_LLOYD_ITALY;
    case BuildType.DAVID_LLOYD_FRANCE:
      return EnquiryBrand.DAVID_LLOYD_FRANCE;
    case BuildType.DAVID_LLOYD_GERMANY:
      return EnquiryBrand.DAVID_LLOYD_GERMANY;
    case BuildType.MERIDIAN:
      return EnquiryBrand.MERIDIAN;
    case BuildType.DAVID_LLOYD_SWITZERLAND:
      return EnquiryBrand.DAVID_LLOYD_SWITZERLAND;
    case BuildType.TOGETHER_FOUNDATION:
      return EnquiryBrand.TOGETHER_FOUNDATION;
    case BuildType.DAVID_LLOYD_LUXEMBOURG:
      return EnquiryBrand.DAVID_LLOYD_LUXEMBOURG;
  }
};

export const getGlobalCmsCollectionLabelForBuildTypeWithBreadcrumbs = (
  buildType: BuildType,
  ...breadcrumbs: string[]
): string =>
  createFileLabelFromParts(getClubDisplayValues(buildType).cmsCollectionName, ...breadcrumbs);

export const getGlobalCmsCollectionLabelForBuildType = (buildType: BuildType): string =>
  getGlobalCmsCollectionLabelForBuildTypeWithBreadcrumbs(buildType);

export const getGlobalCityPageCmsCollectionLabelForBuildType = (buildType: BuildType): string =>
  getGlobalCmsCollectionLabelForBuildTypeWithBreadcrumbs(buildType, 'Cities');

export const getGlobalHelpCentreCategoriesCmsCollectionLabelForBuildType = (
  buildType: BuildType
): string => getGlobalCmsCollectionLabelForBuildTypeWithBreadcrumbs(buildType, 'Help Centre');

export const getMonthlyPaymentTypeForBuildType = (buildType: BuildType): MonthlyPayment => {
  if (buildType === BuildType.DAVID_LLOYD_UK || buildType === BuildType.HARBOUR) {
    return MonthlyPayment.DIRECT_DEBIT;
  } else {
    return MonthlyPayment.SEPA;
  }
};

export const isSingleClubDomain = (buildType: BuildType): boolean => {
  const singleClubDomains: BuildType[] = [
    BuildType.DAVID_LLOYD_GERMANY,
    BuildType.DAVID_LLOYD_FRANCE,
    BuildType.DAVID_LLOYD_IRELAND,
    BuildType.DAVID_LLOYD_LUXEMBOURG,
    BuildType.DAVID_LLOYD_ITALY,
  ];
  return singleClubDomains.includes(buildType);
};

export const getOnlineJoiningEmailForBuildType = (buildType: BuildType): string => {
  if (buildType === BuildType.HARBOUR) {
    return 'online.joining@harbourclub.co.uk';
  }
  return 'online.joining@davidlloyd.co.uk';
};

export const getFeedbackEmailForBuildType = (buildType: BuildType): string => {
  switch (buildType) {
    case BuildType.MERIDIAN:
      return 'appfeedback@davidlloyd.de';
    case BuildType.HARBOUR:
      return 'appfeedback@harbourclub.co.uk';
    default:
      return 'appfeedback@davidlloyd.co.uk';
  }
};

export const getLinkedBuildTypesForBuildType = (buildType: BuildType): BuildType[] => {
  switch (buildType) {
    case BuildType.DAVID_LLOYD_SWITZERLAND:
      return [BuildType.DAVID_LLOYD_FRANCE];
    case BuildType.DAVID_LLOYD_FRANCE:
      return [BuildType.DAVID_LLOYD_SWITZERLAND];
    case BuildType.DAVID_LLOYD_GERMANY:
      return [BuildType.MERIDIAN];
    case BuildType.MERIDIAN:
      return [BuildType.DAVID_LLOYD_GERMANY];
    default:
      return [];
  }
};

export const getDefaultBuildTypeForCountry = (country: Country): BuildType => {
  switch (country) {
    case Country.BELGIUM:
      return BuildType.DAVID_LLOYD_BELGIUM;
    case Country.FRANCE:
      return BuildType.DAVID_LLOYD_FRANCE;
    case Country.GERMANY:
      return BuildType.DAVID_LLOYD_GERMANY;
    case Country.IRELAND:
      return BuildType.DAVID_LLOYD_IRELAND;
    case Country.ITALY:
      return BuildType.DAVID_LLOYD_ITALY;
    case Country.NETHERLANDS:
      return BuildType.DAVID_LLOYD_NETHERLANDS;
    case Country.SPAIN:
      return BuildType.DAVID_LLOYD_SPAIN;
    case Country.SWITZERLAND:
      return BuildType.DAVID_LLOYD_SWITZERLAND;
    case Country.UNITED_KINGDOM:
      return BuildType.DAVID_LLOYD_UK;
    case Country.LUXEMBOURG:
      return BuildType.DAVID_LLOYD_LUXEMBOURG;
  }
};

export const getCountryForBuildType = (buildType: string): Country => {
  switch (buildType as BuildType) {
    case BuildType.DAVID_LLOYD_UK:
    case BuildType.HARBOUR:
    case BuildType.TOGETHER_FOUNDATION:
      return Country.UNITED_KINGDOM;
    case BuildType.DAVID_LLOYD_BELGIUM:
      return Country.BELGIUM;
    case BuildType.DAVID_LLOYD_FRANCE:
      return Country.FRANCE;
    case BuildType.DAVID_LLOYD_GERMANY:
    case BuildType.MERIDIAN:
      return Country.GERMANY;
    case BuildType.DAVID_LLOYD_IRELAND:
      return Country.IRELAND;
    case BuildType.DAVID_LLOYD_LUXEMBOURG:
      return Country.LUXEMBOURG;
    case BuildType.DAVID_LLOYD_ITALY:
      return Country.ITALY;
    case BuildType.DAVID_LLOYD_NETHERLANDS:
      return Country.NETHERLANDS;
    case BuildType.DAVID_LLOYD_SPAIN:
      return Country.SPAIN;
    case BuildType.DAVID_LLOYD_SWITZERLAND:
      return Country.SWITZERLAND;
    default:
      throw new Error(`Unexpected build type ${buildType} when looking up country`);
  }
};

export const getCountryCodeForBuildType = (buildType: string) => {
  const country = getCountryForBuildType(buildType);
  switch (country) {
    case Country.UNITED_KINGDOM:
      return 'GB';
    case Country.BELGIUM:
      return 'BE';
    case Country.FRANCE:
      return 'FR';
    case Country.GERMANY:
      return 'DE';
    case Country.IRELAND:
      return 'IE';
    case Country.ITALY:
      return 'IT';
    case Country.NETHERLANDS:
      return 'NL';
    case Country.SPAIN:
      return 'ES';
    case Country.SWITZERLAND:
      return 'CH';
    default:
      throw new Error(`Unexpected country ${country as string} when looking up country code`);
  }
};
